import { ReviewStatus } from "../../enums/reviews";
import {
  DefaultFetchError,
  FetchDelete,
  FetchGet,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchDelete,
  useFetchGet,
  useFetchUpdate,
} from "../fetch";

export interface ReviewUser {
  id: string;
  role: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatar?: string;
  status: string;
}

export interface ReviewPolymorphicRelation {
  id: string;
  entityName: string;
  user?: ReviewUser;
}

export interface Review {
  id: string;
  rating: number;
  text: string;
  status: ReviewStatus;
  imageUrls: string[];
  removed: boolean;
  user: ReviewUser;
  createdAt: string;
  polymorphicRelation?: ReviewPolymorphicRelation;
}

export interface ReviewTableRow extends Review {
  key: string;
}

export interface ReviewTableData {
  data: ReviewTableRow[];
  total: number;
}

export interface ReviewGetParams
  extends PagingParams, Partial<Omit<Review, "id">> {
  orderByColumn?: keyof Omit<Review, "id">;
}

export type ReviewUpsetParams = Omit<Review, "id">;

export const useReviewsGet = <DD = PagingDataResponse<ReviewUser>>(
  decorateData?: (data: PagingDataResponse<Review>) => DD,
): FetchGet<
  PagingDataResponse<Review>,
  ReviewGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("reviews", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
    config: {
      params: {
        ...{
          orderBy: "ASC",
          orderByColumn: "createdAt",
        },
      },
    },
  });
export type ReviewUpdateParams = Partial<
  Omit<Review, "id" | "user" | "createdAt" | "polymorphicRelation">
> & {
  polymorphicRelationId?: string;
};

export const useReviewUpdate = (
  id?: string,
): FetchUpdate<Review, DefaultFetchError, ReviewUpdateParams> =>
  useFetchUpdate("reviews", id);

export const useReviewDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("reviews", id);

export const useReviewTableGet = () =>
  useReviewsGet<ReviewTableData>(
    ({ data, meta }: PagingDataResponse<Review>): ReviewTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );
